export const HOLD_DAYS = 30
export const DAY_MS = 24 * 60 * 60 * 1000
// September 14, 2026 at 00:00 in New York (EDT), exclusive for legacy purchases.
export const LEGACY_POLICE_CUTOFF_ISO = '2026-09-14T04:00:00.000Z'

/** Accept Firestore timestamps, serialized timestamps, Dates and ISO strings. */
export function envelopeDate(value) {
  try {
    let date
    if (value instanceof Date) date = new Date(value.getTime())
    else if (typeof value?.toDate === 'function') date = value.toDate()
    else if (value && typeof value === 'object' && Number.isFinite(value.seconds)) {
      const nanos = value.nanoseconds ?? 0
      if (!Number.isInteger(value.seconds) || !Number.isInteger(nanos) || nanos < 0 || nanos >= 1e9) return null
      date = new Date(value.seconds * 1000 + nanos / 1e6)
    } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) {
      const [year, month, day] = value.slice(0, 10).split('-').map(Number)
      const [hour, minute, second] = value.slice(11, 19).split(':').map(Number)
      const calendar = new Date(0)
      calendar.setUTCFullYear(year, month, 0)
      if (month < 1 || month > 12 || day < 1 || day > calendar.getUTCDate() || hour > 23 || minute > 59 || second > 59) return null
      date = new Date(value)
    } else return null
    return date instanceof Date && Number.isFinite(date.getTime()) ? new Date(date.getTime()) : null
  } catch { return null }
}

const text = value => typeof value === 'string' && value.trim() ? value.trim() : null
const pending = () => ({ reportStatus: 'pending', reportedAt: null, holdStartedAt: null,
  holdUntil: null, reportReference: null, reportConfirmedBy: null })

const LEGACY_FIELDS = new Set(['schemaVersion', 'source', 'channel', 'verification', 'declaredBy', 'recordedBy',
  'recordedAt', 'migrationId', 'cutoffExclusive', 'holdBasis', 'purchaseAt', 'holdUntil'])
const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i
const serviceAccount = value => typeof value === 'string' &&
  /^[a-z0-9][a-z0-9._+-]*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+gserviceaccount\.com$/i.test(value)

function legacyDate(value) {
  const date = envelopeDate(value)
  if (!date) return null
  if (value && typeof value === 'object' && ('seconds' in value || 'nanoseconds' in value)) {
    const nanos = value.nanoseconds ?? 0
    if (!Number.isInteger(value.seconds) || !Number.isInteger(nanos) || nanos < 0 || nanos >= 1e9 ||
        new Date(value.seconds * 1000 + nanos / 1e6).getTime() !== date.getTime()) return null
  }
  return date
}

// Retain exact equality when both original Firestore timestamps have sub-ms
// precision. Canonical Date results remain usable by the client normalizer.
function timestampIdentity(value, date) {
  if (value && typeof value === 'object' && Number.isInteger(value.seconds) &&
      Number.isInteger(value.nanoseconds ?? 0)) return `${value.seconds}:${value.nanoseconds ?? 0}`
  const seconds = Math.floor(date.getTime() / 1000)
  return `${seconds}:${(date.getTime() - seconds * 1000) * 1e6}`
}

/** Owner-attested reporting to local police is separate from any RISS receipt.
 * Only the server-owned migration record establishes this historical pathway;
 * old top-level purchase/hold dates, or a status label, never establish it.
 */
export function getLegacyPoliceReport(raw) {
  try {
    const report = raw?.legacyPoliceReport
    if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !Object.hasOwn(raw, 'legacyPoliceReport') ||
        !report || typeof report !== 'object' || Array.isArray(report) ||
        Object.keys(report).length !== LEGACY_FIELDS.size || Object.keys(report).some(key => !LEGACY_FIELDS.has(key)) ||
        report.schemaVersion !== 1 || report.source !== 'local_police' || report.channel !== 'email' ||
        report.verification !== 'owner_attested' || report.declaredBy !== 'Moses' ||
        !serviceAccount(report.recordedBy) || report.holdBasis !== 'purchase_date' ||
        report.cutoffExclusive !== LEGACY_POLICE_CUTOFF_ISO ||
        typeof report.migrationId !== 'string' || !UUID.test(report.migrationId) ||
        !/[1-9a-f]/i.test(report.migrationId.replaceAll('-', ''))) return null
    // Reject both valid and incomplete delivery metadata instead of combining
    // two unrelated kinds of evidence. Derived normalized legacy fields are OK.
    if (![undefined, null, 'pending', 'legacy_reported'].includes(raw.reportStatus) ||
        raw.reportedAt != null || raw.reportConfirmation != null || raw.reportDelivery != null ||
        (raw.reportSource != null && raw.reportSource !== 'local_police') || raw.reportReference != null) return null
    const createdAt = legacyDate(raw.createdAt)
    const purchaseAt = legacyDate(report.purchaseAt)
    const recordedAt = legacyDate(report.recordedAt)
    const holdUntil = legacyDate(report.holdUntil)
    const cutoff = Date.parse(LEGACY_POLICE_CUTOFF_ISO)
    if (!createdAt || !purchaseAt || !recordedAt || !holdUntil ||
        timestampIdentity(raw.createdAt, createdAt) !== timestampIdentity(report.purchaseAt, purchaseAt) ||
        purchaseAt.getTime() >= cutoff || recordedAt.getTime() < cutoff || recordedAt < purchaseAt ||
        holdUntil.getTime() !== purchaseAt.getTime() + HOLD_DAYS * DAY_MS) return null
    if (raw.holdStartedAt != null) {
      const derivedStart = legacyDate(raw.holdStartedAt)
      if (!derivedStart || derivedStart.getTime() !== purchaseAt.getTime()) return null
    }
    // Do not expose mutable Firestore/Date input objects through returned data.
    return {
      schemaVersion: 1, source: 'local_police', channel: 'email', verification: 'owner_attested', declaredBy: 'Moses',
      recordedBy: report.recordedBy, recordedAt, migrationId: report.migrationId.toLowerCase(),
      cutoffExclusive: LEGACY_POLICE_CUTOFF_ISO, holdBasis: 'purchase_date', purchaseAt, holdUntil,
    }
  } catch { return null }
}

/** Legacy purchase dates and holdUntil alone are never evidence of a report. */
export function getEnvelopeLifecycle(raw) {
  const legacy = getLegacyPoliceReport(raw)
  if (legacy) return {
    reportStatus: 'legacy_reported', reportedAt: null, holdStartedAt: new Date(legacy.purchaseAt.getTime()),
    holdUntil: new Date(legacy.holdUntil.getTime()), reportReference: null, reportConfirmedBy: legacy.declaredBy,
    reportSource: 'local_police', reportChannel: 'email', legacyMigrationId: legacy.migrationId,
    legacyRecordedAt: new Date(legacy.recordedAt.getTime()), legacyHoldBasis: legacy.holdBasis,
  }
  const confirmation = raw?.reportConfirmation
  // Keep previously recorded manual holds intact. New reporting starts the
  // clock on successful delivery of every XML file, not on RISS acceptance.
  const delivered = confirmation?.method === 'cloud_delivery'
    && confirmation.httpStatus === 201 && confirmation.accepted === false
    && Number.isInteger(confirmation.fileCount) && confirmation.fileCount > 0
    && confirmation.uploadedFiles === confirmation.fileCount
  if (raw?.reportStatus !== 'reported' || confirmation?.environment !== 'production' ||
      (confirmation?.method !== 'manual_confirmation' && !delivered)) return pending()
  const reportedAt = envelopeDate(raw.reportedAt)
  const holdStartedAt = envelopeDate(raw.holdStartedAt)
  const storedUntil = envelopeDate(raw.holdUntil)
  const confirmedAt = envelopeDate(confirmation.confirmedAt)
  const reportReference = text(confirmation.reference)
  const reportConfirmedBy = text(confirmation.confirmedBy)
  if (!reportedAt || !holdStartedAt || !storedUntil || !confirmedAt || !reportReference || !reportConfirmedBy) return pending()
  const start = reportedAt.getTime()
  const until = start + HOLD_DAYS * DAY_MS
  if (holdStartedAt.getTime() !== start || confirmedAt.getTime() !== start ||
      storedUntil.getTime() !== until || !Number.isFinite(new Date(until).getTime())) return pending()
  return { reportStatus: 'reported', reportedAt, holdStartedAt, holdUntil: new Date(until), reportReference, reportConfirmedBy }
}

export const isAwaitingReport = raw => getEnvelopeLifecycle(raw).reportStatus === 'pending'

function timedLifecycle(raw, now) {
  const lifecycle = getEnvelopeLifecycle(raw)
  const clock = envelopeDate(now)
  if (!['reported', 'legacy_reported'].includes(lifecycle.reportStatus) || !clock || lifecycle.holdStartedAt > clock ||
      (lifecycle.reportStatus === 'legacy_reported' && lifecycle.legacyRecordedAt > clock)) return null
  return { ...lifecycle, now: clock }
}

export function holdProgress(raw, now = new Date()) {
  const lifecycle = timedLifecycle(raw, now)
  return lifecycle ? Math.max(0, Math.min(1, (lifecycle.now - lifecycle.holdStartedAt) / (HOLD_DAYS * DAY_MS))) : 0
}

export function isEnvelopeReady(raw, now = new Date()) {
  const lifecycle = timedLifecycle(raw, now)
  return !!lifecycle && lifecycle.now >= lifecycle.holdUntil
}

export function daysUntilReady(raw, now = new Date()) {
  const lifecycle = timedLifecycle(raw, now)
  return lifecycle ? Math.max(0, Math.ceil((lifecycle.holdUntil - lifecycle.now) / DAY_MS)) : null
}
